import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import adminApi, {
  clearStoredToken,
  getStoredToken,
  isTokenExpired,
  storeToken,
} from "@/lib/adminApi";

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = getStoredToken();
    if (!token || isTokenExpired()) {
      clearStoredToken();
      setUser(null);
      setChecking(false);
      return;
    }
    try {
      const { data } = await adminApi.get("/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (username, password) => {
    const { data } = await adminApi.post("/login", { username, password });
    storeToken(data.token, data.expires_at);
    const me = await adminApi.get("/me");
    setUser(me.data);
    return me.data;
  }, []);

  const logout = useCallback(() => {
    clearStoredToken();
    setUser(null);
    if (typeof window !== "undefined") {
      window.location.replace("/admin/login");
    }
  }, []);

  const value = useMemo(
    () => ({ user, checking, isAuthenticated: !!user, login, logout }),
    [user, checking, login, logout]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
